/**
 * SoundEngine
 *
 * Provides audio feedback for trading actions using the Web Audio API.
 */

interface WebkitWindow extends Window {
    webkitAudioContext?: typeof AudioContext;
}

/**
 * Configuration for sound engine.
 */
export interface SoundEngineConfig {
    /** Master volume (0-1) */
    masterVolume?: number;
    /** Enable/disable sounds */
    enabled?: boolean;
}

const DEFAULT_SOUND_CONFIG: Required<SoundEngineConfig> = {
    masterVolume: 0.25,
    enabled: true,
};

/**
 * SoundEngine class.
 * Can be instantiated with custom configuration.
 */
export class SoundEngine {
    private ctx: AudioContext | null = null;
    private masterGain: GainNode | null = null;
    private config: Required<SoundEngineConfig>;

    constructor(config?: SoundEngineConfig) {
        this.config = { ...DEFAULT_SOUND_CONFIG, ...config };
    }

    private init(): void {
        if (!this.ctx) {
            const AudioContextClass =
                window.AudioContext ||
                (window as WebkitWindow).webkitAudioContext;
            if (AudioContextClass) {
                this.ctx = new AudioContextClass();
                this.masterGain = this.ctx.createGain();
                this.masterGain.gain.value = this.config.masterVolume;
                this.masterGain.connect(this.ctx.destination);
            }
        }
        if (this.ctx?.state === 'suspended') {
            this.ctx.resume().catch(() => {});
        }
    }

    /**
     * Update configuration at runtime.
     */
    public setConfig(config: Partial<SoundEngineConfig>): void {
        this.config = { ...this.config, ...config };
        if (this.masterGain && config.masterVolume !== undefined) {
            this.masterGain.gain.value = config.masterVolume;
        }
    }

    /**
     * Play position open sound.
     */
    public playOpen(): void {
        if (!this.config.enabled) return;
        this.init();
        if (!this.ctx || !this.masterGain) return;

        const t = this.ctx.currentTime;

        // High-tech "Blip" / Activation sound
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();

        osc.type = 'sine';
        // Rapid pitch ramp up
        osc.frequency.setValueAtTime(600, t);
        osc.frequency.exponentialRampToValueAtTime(1200, t + 0.1);

        // Short, sharp envelope
        gain.gain.setValueAtTime(0, t);
        gain.gain.linearRampToValueAtTime(0.5, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.01, t + 0.15);

        osc.connect(gain);
        gain.connect(this.masterGain);

        osc.start(t);
        osc.stop(t + 0.15);
    }

    /**
     * Play profit/win sound.
     */
    public playProfit(): void {
        if (!this.config.enabled) return;
        this.init();
        if (!this.ctx || !this.masterGain) return;

        const t = this.ctx.currentTime;

        // Harmonious Ascending Arpeggio (C Major 7)
        // Notes: C5, E5, G5, B5
        const notes = [523.25, 659.25, 783.99, 987.77];

        notes.forEach((freq, i) => {
            const osc = this.ctx!.createOscillator();
            const gain = this.ctx!.createGain();

            osc.type = 'triangle'; // Softer tone than sine
            osc.frequency.value = freq;

            const startTime = t + i * 0.06; // Staggered start
            const duration = 0.3;

            gain.gain.setValueAtTime(0, startTime);
            gain.gain.linearRampToValueAtTime(0.15, startTime + 0.05);
            gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

            osc.connect(gain);
            gain.connect(this.masterGain!);

            osc.start(startTime);
            osc.stop(startTime + duration);
        });
    }

    /**
     * Play loss sound.
     */
    public playLoss(): void {
        if (!this.config.enabled) return;
        this.init();
        if (!this.ctx || !this.masterGain) return;

        const t = this.ctx.currentTime;

        // Dissonant "Power Down" Effect
        const osc1 = this.ctx.createOscillator();
        const osc2 = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        const filter = this.ctx.createBiquadFilter();

        // Two detuned low oscillators
        osc1.type = 'sawtooth';
        osc1.frequency.setValueAtTime(150, t);
        osc1.frequency.exponentialRampToValueAtTime(40, t + 0.4);

        osc2.type = 'square';
        osc2.frequency.setValueAtTime(145, t); // Slight dissonance
        osc2.frequency.exponentialRampToValueAtTime(35, t + 0.4);

        // Filter sweep (Closing the filter)
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(800, t);
        filter.frequency.exponentialRampToValueAtTime(50, t + 0.4);

        // Volume envelope
        gain.gain.setValueAtTime(0.2, t);
        gain.gain.exponentialRampToValueAtTime(0.001, t + 0.4);

        osc1.connect(filter);
        osc2.connect(filter);
        filter.connect(gain);
        gain.connect(this.masterGain);

        osc1.start(t);
        osc2.start(t);
        osc1.stop(t + 0.4);
        osc2.stop(t + 0.4);
    }
}

/**
 * Factory function to create a new SoundEngine instance.
 */
export const createSoundEngine = (config?: SoundEngineConfig): SoundEngine => {
    return new SoundEngine(config);
};

/**
 * Default singleton instance for backward compatibility.
 */
export const soundEngine = createSoundEngine();
